import { Icons, Tooltip } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

/** `current` names the page the panel is open on; `carried` names context brought over from
 * earlier, so it reads a step quieter than the page in front of the user. */
export type TChatPanelPillVariant = 'current' | 'carried';

interface ChatPanelPillProps {
  label: string;
  /** Hover text; defaults to the label so a truncated label can still be read in full. */
  tooltip?: string;
  variant?: TChatPanelPillVariant;
  /** Omitted when the pill states something the user cannot turn off. */
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}

const VARIANT_CLASS_NAMES: Record<TChatPanelPillVariant, string> = {
  current: 'bg-bg-primary text-text-primary',
  carried: 'bg-bg-light text-text-secondary',
};

/** The small context pill that sits above the composer (Figma Chat / Context pill). It hugs its
 * label rather than filling the panel, so several can sit side by side and none of them reads as a
 * banner.
 *
 * Not kakashi's `Badge`: that one keeps its label at full width (`shrink-0`) and has no accessible
 * name on its close button, and this pill needs a title-length label to truncate inside a 360px
 * column with a labelled dismiss. */
export const ChatPanelPill = ({
  label,
  tooltip = label,
  variant = 'current',
  onDismiss,
  dismissLabel,
  className,
}: ChatPanelPillProps) => (
  <div
    className={cn(
      'flex min-w-0 max-w-full items-center gap-sm rounded-sm border-1/2 border-border-secondary py-xs pl-md pr-[10px]',
      VARIANT_CLASS_NAMES[variant],
      className
    )}
  >
    <Tooltip content={tooltip} asChild>
      <span className="min-w-0 truncate font-mulish text-xs font-medium leading-[15px] tracking-[-2%]">
        {label}
      </span>
    </Tooltip>
    {onDismiss && (
      <button
        type="button"
        aria-label={dismissLabel}
        onClick={onDismiss}
        className="flex size-3 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-none text-text-secondary transition-colors hover:text-text-primary"
      >
        <Icons.XClose className="size-3" aria-hidden="true" />
      </button>
    )}
  </div>
);
